'use-strict';

import { IIntervalService, IPromise, IScope } from 'angular';
import { Constants, SettingsAPIService } from '../../../shared/api/settings-api.service';
import { ConfigService } from '../../../shared/config/config.service';
import { InterfaceService } from '../../../shared/interface/interface.service';
import { EExternalAPI } from '../../../src/app/@shared-types/api';
import { LinkEntryAPIConfig } from '../../../src/app/@shared-types/api/stellantis';
import { ImportRun, ImportRunPhase } from '../../../src/app/@shared-types/api/import-run';
import LogService from '../../../src/app/shared/services/log';
import { ToastService, ToastType } from '../../../ui-components/toast/toast.service';
import { EConstantType } from '../../../src/app/@shared-types/config/enums';

const POLLING_INTERVAL = 3000;

const PHASES: { [phase in ImportRunPhase]: string } = {
  LOGIN: 'Anmeldung bei Link Entry',
  SEARCH: 'Suche nach Fahrzeugen',
  DETAILS: 'Abruf der Fahrzeugdetails',
  IMPORT: 'Speichern der Fahrzeuge',
  WLTP: 'Abruf der WLTP-Daten',
};

interface MissingVehicle {
  vehicleId: string,
  orderNumber: string,
  status: 'MISSING',
  vin?: string,
}

/* @ngInject */
export default class LinkDashboardController {
  private $scope: IScope;

  public linkEntryAPIConfig: LinkEntryAPIConfig | null = null;

  public importing = false;

  public importRun: ImportRun | null = null;

  private polling: IPromise<unknown> | null = null;

  private $interval: IIntervalService;

  private settingsApiService: SettingsAPIService;

  private interfaceService: InterfaceService;

  private toastService: ToastService;

  private logService: LogService;

  public locations: { [key: string]: string } | null;

  public saving = false;

  public notificationAdmins: string | null = null;

  public missingVehicles: MissingVehicle[] | null = null;

  public vehicleTypes: { [key: string]: string } | null;

  public multiAccounts: { [key: number]: boolean } = {};

  constructor(
    $scope: IScope,
    $interval: IIntervalService,
    settingsApiService: SettingsAPIService,
    interfaceService: InterfaceService,
    toastService: ToastService,
    configService: ConfigService,
    logService: LogService,
  ) {
    this.settingsApiService = settingsApiService;
    this.interfaceService = interfaceService;
    this.$scope = $scope;
    this.$interval = $interval;
    this.toastService = toastService;
    this.logService = logService;

    this.getLinkEntryConfig();
    this.getImportStatus();
    this.getAdmins();
    this.getMissingVehicles();
    this.locations = configService.get('general_locations');
    this.vehicleTypes = configService.get('sales_car_types');

    this.$scope.$on('$destroy', () => this.stopPolling());
  }

  public phaseLabel = (run: ImportRun): string => (run.progress ? PHASES[run.progress.phase] : 'Import wird gestartet');

  private stopPolling = (): void => {
    if (this.polling) this.$interval.cancel(this.polling);
    this.polling = null;
  };

  /** While an import is running, we poll its progress. When it finishes, we show the result. */
  private getImportStatus = async (): Promise<void> => {
    const run = await this.settingsApiService.getLinkEntryImportStatus()
      .catch((e) => {
        this.logService.error(e);
        return this.importRun;
      });
    const wasRunning = this.importing;

    this.importRun = run;
    this.importing = run?.state === 'RUNNING';

    if (this.importing && !this.polling) {
      this.polling = this.$interval(() => this.getImportStatus(), POLLING_INTERVAL);
    }

    if (!this.importing && wasRunning) {
      this.stopPolling();
      this.getLinkEntryConfig();
      if (run?.state === 'DONE') this.toastService.toast(ToastType.SUCCESS, 'Import von Link Entry Fahrzeugen durchgeführt.');
      if (run?.state === 'FAILED') this.toastService.toast(ToastType.ERROR, 'Fehler beim Import von Link Entry.');
    }

    this.interfaceService.applyScope(this.$scope);
  };

  private getLinkEntryConfig = async (): Promise<void> => {
    const configs = await this.settingsApiService
      .getAPIConfig<LinkEntryAPIConfig[]>(EExternalAPI.LINK_ENTRY).catch();

    [this.linkEntryAPIConfig] = configs;

    this.linkEntryAPIConfig.accounts.forEach((account, index) => {
      if (account.multiAccountUsernames) this.multiAccounts[index] = true;
    });

    this.interfaceService.applyScope(this.$scope);
  };

  private getMissingVehicles = async (): Promise<void> => {
    const constants = await this.settingsApiService.getAllConstants();
    const missingVehicles = constants.find((c) => c.attribute === 'link_entry_api_missing_cars')?.value as MissingVehicle[] || null;
    this.missingVehicles = missingVehicles;
  };

  private getAdmins = async () => {
    this.settingsApiService.getAllConstants()
      .then((constants) => {
        const admins = (constants as Constants[]).find((c) => c.attribute === 'link_entry_api_missing_cars_admins');
        if (!admins) {
          this.notificationAdmins = '';
        } else {
          this.notificationAdmins = (admins.value as number[]).join(',');
        }
      })
      .catch((e) => this.logService.error(e));
  };

  public saveAdmins = (): void => {
    this.settingsApiService.updateConstants([{
      key: 'link_entry_api_missing_cars_admins',
      value: this.notificationAdmins?.split(',').map((a) => parseInt(a, 10)) || [],
    }])
      .then(() => {
        this.toastService.toast(ToastType.SUCCESS, 'Benutzer gespeichert.');
      }).catch((e) => {
        this.toastService.toast(ToastType.ERROR, 'Fehler beim Speichern der Benutzer.');
        this.logService.error(e);
      });
  };

  public updateConfig = (
    config: unknown,
  ): Promise<void> => this.settingsApiService.updateAPIConfig(EExternalAPI.LINK_ENTRY, config)
    .then(() => this.toastService.toast(ToastType.SUCCESS, 'Einstellungen gespeichert.'));

  public startImport = (): void => {
    this.importing = true;
    this.settingsApiService.startLinkEntryImport()
      .then(() => this.getImportStatus())
      .catch((e) => {
        if (e.status === 409) {
          this.toastService.toast(ToastType.WARNING, 'Ein Import von Link Entry läuft bereits.');
        } else {
          this.toastService.toast(ToastType.ERROR, 'Fehler beim manuellen Import von Link Entry.');
          this.logService.error(e);
        }
        this.importing = false;
        this.getImportStatus();
      });
  };

  public removeVehicleFromMissingList = (vehicleId: string): void => {
    if (!this.missingVehicles) return;
    this.missingVehicles = this.missingVehicles.filter((v) => v.vehicleId !== vehicleId);
  };

  public saveMissingVehicles = async (): Promise<void> => {
    if (!this.missingVehicles) return;

    this.saving = true;

    await this.settingsApiService.updateConstants([{
      key: EConstantType.LINK_ENTRY_MISSING_VEHICLES,
      value: this.missingVehicles,
    }]).then(() => {
      this.toastService.toast(ToastType.SUCCESS, 'Liste fehlender Fahrzeuge gespeichert.');
    }).catch((e) => {
      this.toastService.toast(ToastType.ERROR, 'Fehler Speichern.');
      this.logService.error(e);
    });

    this.saving = false;
  };
}
